import { getMerchantNotificationAvatarEmilyBlunt } from '@assets/illustrations';

const DEFAULT_AVATAR = getMerchantNotificationAvatarEmilyBlunt();

const pickFirst = (...values) => values.find((value) => value != null && value !== '');

const toNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const TIME_UNITS = [
  { limit: 60, divisor: 1, label: 'second' },
  { limit: 3600, divisor: 60, label: 'minute' },
  { limit: 86400, divisor: 3600, label: 'hour' },
  { limit: 604800, divisor: 86400, label: 'day' },
  { limit: 2629800, divisor: 604800, label: 'week' },
  { limit: 31557600, divisor: 2629800, label: 'month' },
];

/** Converts an ISO/date string into a short relative time label. */
const formatTimeAgo = (value) => {
  if (!value) return '';

  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return String(value);

  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 30) return 'Just now';

  const unit = TIME_UNITS.find((item) => seconds < item.limit);
  if (!unit) {
    const years = Math.floor(seconds / 31557600);
    return `${years} year${years > 1 ? 's' : ''} ago`;
  }

  const amount = Math.floor(seconds / unit.divisor);
  return `${amount} ${unit.label}${amount > 1 ? 's' : ''} ago`;
};

/** Maps a numeric rating to a badge sentiment variant. */
export const getReviewSentiment = (rating) => {
  const value = toNumber(rating, 0);
  if (value >= 4) return 'positive';
  if (value === 3) return 'warning';
  return 'negative';
};

const extractList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.reviews?.content)) return data.reviews.content;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.reviews)) return data.reviews;
  if (Array.isArray(data?.list)) return data.list;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const extractPhotoUrls = (review = {}) => {
  const source = pickFirst(review.photos, review.images, review.attachments, review.media) || [];
  if (!Array.isArray(source)) return [];

  return source
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      if (!item || typeof item !== 'object') return '';
      return String(
        pickFirst(
          item.url,
          item.photoUrl,
          item.imageUrl,
          item.signedUrl,
          item.fileUrl,
          item.src,
          item.path,
        ) || '',
      ).trim();
    })
    .filter(Boolean);
};

const mapReview = (review = {}, index = 0) => {
  const rating = toNumber(
    pickFirst(review.rating, review.stars, review.score, review.ratingValue),
    0,
  );

  const images = extractPhotoUrls(review);

  const createdAt =
    pickFirst(
      review.reviewDate,
      review.createdAt,
      review.created_at,
      review.date,
      review.reviewedAt,
    ) || '';

  return {
    id: String(pickFirst(review.id, review.reviewId, review._id, index)),
    name:
      pickFirst(
        review.userName,
        review.name,
        review.customerName,
        review.reviewerName,
        review.authorName,
        review.user?.name,
      ) || 'Anonymous',
    avatar:
      pickFirst(
        review.userPhoto,
        review.avatar,
        review.userImage,
        review.profilePicUrl,
        review.photoUrl,
        review.image,
        review.user?.avatar,
        review.user?.profilePicUrl,
      ) || DEFAULT_AVATAR,
    rating,
    text:
      pickFirst(
        review.reviewComments,
        review.text,
        review.comment,
        review.review,
        review.message,
        review.description,
      ) || '',
    createdAt,
    timeAgo: formatTimeAgo(createdAt),
    images,
    sentiment: getReviewSentiment(rating),
  };
};

const STAR_WORD_KEYS = {
  5: 'fiveStar',
  4: 'fourStar',
  3: 'threeStar',
  2: 'twoStar',
  1: 'oneStar',
};

/** Reads a rating count map (word keys, numeric keys, or array) into a 5..1 distribution. */
const mapDistribution = (source = {}) => {
  const getCount = (star) => {
    if (Array.isArray(source)) {
      const match = source.find(
        (item) => toNumber(item?.star ?? item?.rating ?? item?.value) === star,
      );
      return toNumber(match?.count ?? match?.total ?? match?.value, 0);
    }
    return toNumber(
      source?.[STAR_WORD_KEYS[star]] ??
        source?.[star] ??
        source?.[`${star}`] ??
        source?.[`star${star}`],
      0,
    );
  };

  return [5, 4, 3, 2, 1].map((star) => ({
    star,
    label: `${star} star`,
    count: getCount(star),
  }));
};

/**
 * Normalizes POST /merchant/review/list response for the Reviews & Ratings tab.
 */
export const mapReviews = (payload = {}) => {
  const data = payload?.data ?? payload ?? {};
  const pageData = data.reviews && !Array.isArray(data.reviews) ? data.reviews : data;

  const ratingCount = pickFirst(
    data.ratingCount,
    data.ratingCounts,
    data.ratingDistribution,
    data.distribution,
    data.ratings,
  );

  const distribution = mapDistribution(ratingCount || {});
  const distributionTotal = distribution.reduce((sum, item) => sum + item.count, 0);

  const totalReviews = toNumber(
    pickFirst(
      ratingCount?.totalReviews,
      data.totalReviews,
      pageData?.totalElements,
      data.total,
      data.totalCount,
      data.count,
    ),
    distributionTotal,
  );

  const averageRating = toNumber(
    pickFirst(
      ratingCount?.averageRating,
      data.averageRating,
      data.avgRating,
      data.rating,
      data.overallRating,
    ),
    0,
  );

  const reviews = extractList(data).map(mapReview);

  return {
    averageRating,
    totalReviews,
    distribution,
    reviews,
    page: toNumber(pickFirst(pageData?.page, data.currentPage, data.pageNumber), 0),
    limit: toNumber(pickFirst(pageData?.size, data.limit, data.pageSize, data.perPage), 15),
    totalPages: toNumber(pickFirst(pageData?.totalPages, data.totalPages, data.pageCount), 1),
  };
};

export default mapReviews;
